/**
 * conversationSharing — the link shape and the invite call behind "share this
 * Ellie conversation".
 *
 * Two halves that must not drift apart, hence one file:
 *
 *   1. `buildConversationShareUrl` — the URL we hand to the OS share sheet.
 *   2. `extractConversationInviteNav` — the matcher that turns that same URL
 *      back into a navigation target when someone taps it.
 *
 * The shape is NOT ours to pick. invite-participants
 * (backend/api/client/controllers/conversations/invite-participants/index.js)
 * already emails
 *
 *     https://{APP_BASE}/project/{project_id}/chat?conversation_id={conversation_id}
 *
 * and that endpoint is deployed. APP_BASE (backend/shared.yml) resolves to
 * development.eleplan.org / app.eleplan.com — byte-identical to
 * `config.webApp.baseUrl` here. So a link we generate and a link the backend
 * emails are the same string, and one matcher handles both. If the two ever
 * diverge, invited people land somewhere the app doesn't recognise, which is
 * precisely the failure this pairing exists to prevent.
 *
 * Note the path says `/chat`, not `/conversation/:id` — that's the legacy web
 * route, and it is what is already going out in email. We resolve it to the
 * native Conversation screen at the point of navigation rather than minting a
 * second URL shape the backend knows nothing about.
 */

import config from '../config';
import { apiPost } from './apiClient';
import { getSessionIds } from '../store/actions/_sessionIds';

/**
 * Where a participant came from. Mirrors the legacy web client's payload
 * exactly (frontend/client/src/Components/ConversationInviteModal), because
 * the backend resolves each participant by trying `users` on
 * `cognito_id OR lower(email)` and then falling back to `contacts` on `uid` —
 * so which keys are present is what decides whether an invite lands.
 */
export type ConversationParticipantType = 'member' | 'contact' | 'email';

export interface ConversationParticipant {
  type: ConversationParticipantType;
  email: string;
  /** Account members only — the backend's first-choice lookup key. */
  cognito_id?: string;
  /** Contacts only — the backend's `contacts` fallback key. */
  uid?: string;
  first_name?: string;
  last_name?: string;
}

export interface InviteConversationResult {
  success: boolean;
  invitedCount: number;
  failedCount: number;
  /** Emails (or the literal 'Invalid participant (no email)') the send missed. */
  failed: string[];
  message?: string;
}

/** Deliberately permissive — matches the legacy web `verifyEmailFormat` gate. */
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const isValidInviteEmail = (value: string): boolean =>
  EMAIL_RX.test(String(value || '').trim());

/**
 * The canonical share URL for a conversation — identical to the one
 * invite-participants puts in the `conversation_invite` email.
 */
export const buildConversationShareUrl = (
  projectId: number | string,
  conversationId: string,
): string =>
  `${config.webApp.baseUrl}/project/${projectId}/chat?conversation_id=${encodeURIComponent(conversationId)}`;

/**
 * The inverse: recognise a conversation-invite link and pull out where it
 * points. Returns null for anything else.
 *
 * Kept here rather than inline in RootNavigator (where its sibling extractors
 * live) so it can never drift from `buildConversationShareUrl` above.
 *
 * Accepts the universal-link and custom-scheme forms alike, and tolerates the
 * `?conversation_id=` param sitting anywhere in the query string — mail
 * clients and link wrappers reorder and re-encode freely.
 */
export const extractConversationInviteNav = (
  url: string | null | undefined,
): { projectId: number; conversationId: string } | null => {
  if (!url) return null;
  const path = url.match(/\/project\/(\d+)\/chat(?:[/?#]|$)/);
  if (!path) return null;
  const conversation = url.match(/[?&]conversation_id=([^&#]+)/);
  if (!conversation) return null;
  const conversationId = decodeURIComponent(conversation[1]).trim();
  if (!conversationId) return null;
  return { projectId: parseInt(path[1], 10), conversationId };
};

/**
 * POST /conversation/{conversation_id}/invite/{project_id}/{account_id}/{cognito_id}
 *
 * Emails every participant the `conversation_invite` template with a link back
 * to this conversation. The endpoint is partial-success by design: it walks the
 * list, emails whoever resolves, and reports the rest in `failed` rather than
 * failing the whole request — so a bad address among five good ones costs one
 * invite, not five. Callers should surface `failedCount`, not just `success`.
 */
export async function inviteConversationParticipants(
  conversationId: string,
  projectId: number | string,
  participants: ConversationParticipant[],
): Promise<InviteConversationResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, invitedCount: 0, failedCount: 0, failed: [], message: 'Not authenticated.' };
  }

  const res = await apiPost(
    `/conversation/${conversationId}/invite/${projectId}/${accountId}/${cognitoId}`,
    { participants },
  );

  if (!res?.success) {
    return {
      success: false,
      invitedCount: 0,
      failedCount: 0,
      failed: [],
      message: res?.message || 'Failed to send invites.',
    };
  }

  const payload = res.payload || {};
  return {
    success: true,
    invitedCount: Number(payload.invited_count) || 0,
    failedCount: Number(payload.failed_count) || 0,
    failed: Array.isArray(payload.failed) ? payload.failed : [],
    message: res.message,
  };
}
